import * as React from "react"
import { createPortal } from 'react-dom'
import { cn } from "@/lib/utils"

export interface DropdownItem { label: React.ReactNode; onSelect?: ()=>void; disabled?: boolean }
export interface DropdownMenuProps {
  trigger: React.ReactNode
  items: DropdownItem[]
  align?: 'start'|'center'|'end'
  side?: 'down'|'up'
}

export function DropdownMenu({ trigger, items, align='start', side='down' }: DropdownMenuProps) {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLSpanElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const [pos, setPos] = React.useState<{top:number,left:number,minWidth:number}>({ top:0, left:0, minWidth: 0 })

  const toggle = () => {
    setOpen(v=>{
      const nv = !v
      if (nv) recalc()
      return nv
    })
  }

  function recalc() {
    const t = triggerRef.current
    if (!t) return
    const r = t.getBoundingClientRect()
    // For fixed positioning, use viewport coordinates (no scroll offsets)
    let top = r.bottom + 6
    if (side === 'up') top = r.top - 6
    let left = r.left
    const minWidth = r.width
    setPos({ top, left, minWidth })
    // adjust to align end/center after mount
    requestAnimationFrame(()=>{
      const m = menuRef.current
      if (!m) return
      let L = left
      if (align==='end') L = r.right - m.offsetWidth
      if (align==='center') L = r.left + r.width/2 - m.offsetWidth/2
      const T = side==='up' ? (r.top - m.offsetHeight - 6) : (r.bottom + 6)
      setPos({ top: T, left: L, minWidth })
    })
  }

  React.useEffect(()=>{
    if (!open) return
    const onDoc = (e:MouseEvent)=>{
      const t = e.target as Node
      if (!menuRef.current || !triggerRef.current) return
      if (!menuRef.current.contains(t) && !triggerRef.current.contains(t)) setOpen(false)
    }
    const onWin = ()=> recalc()
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('scroll', onWin, true)
    window.addEventListener('resize', onWin)
    return ()=>{
      document.removeEventListener('mousedown', onDoc)
      window.removeEventListener('scroll', onWin, true)
      window.removeEventListener('resize', onWin)
    }
  }, [open])

  const menuNode = open ? createPortal(
    <div ref={menuRef}
      className="fixed z-[100000] min-w-40 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ top: pos.top, left: pos.left, minWidth: Math.max(160, pos.minWidth) }}
    >
      {items.map((it,idx)=> (
        <button
          key={idx}
          disabled={it.disabled}
          className={cn("block w-full rounded px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50")}
          onClick={() => {
            try {
              it.onSelect?.()
            } finally {
              setOpen(false)
            }
          }}
        >
          {it.label}
        </button>
      ))}
    </div>, document.body) : null

  const handleTriggerClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
    // Impede que o clique no trigger borbulhe para cont�ineres pai (como cards clic�veis)
    e.stopPropagation()
    toggle()
  }

  return (
    <span
      ref={triggerRef}
      onClick={handleTriggerClick}
      className="inline-block align-middle"
    >
      {trigger}
      {menuNode}
    </span>
  )
}
